import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import Carousel from "./Carousel";
import YouTube from "./YouTube";
import ThreeSixty from "../3DModel/ThreeSixty";
import Icon360 from "../shared/Icons/360Icon";
import YouTubeIcon from "../shared/Icons/YouTubeIcon";

import Typography from "../shared/Typography";
import CloseIcon from "../shared/Icons/Close";
import Icon from "@components/shared/Icons/Icon";
import ErrorBoundary from "../shared/ErrorBoundary";
import gtm from "@gtm/core";

import { usePreconfigurationsSelector } from "@redux/reducers/appConfig";
import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";
import { getCarImagePath } from "@shared/v2/lib/getCarImagePath";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { setLastChangedAtAction } from "@redux/actions/deprecatedActions";

import { updateCarPreconfigurationAction } from "@redux/actions/dealActions";

import { openThreeSixtyEvent } from "@gtm/events/homepage";

import { CAR_GALLERY_360_ICON } from "@shared/constants";

const CarGallery = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const preconfigurations = usePreconfigurationsSelector();

  const activePreconfiguration = useActiveCarPreconfigurationSelector();
  const [threeSixtyOpen, setThreeSixtyOpen] = useState(false);
  const [youTubeVideoOpen, setYouTubeVideoOpen] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(
    preconfigurations.findIndex(
      config => config.id === activePreconfiguration.id
    )
  );

  const toggleThreeSixtyOpen = () => {
    gtm.fire(
      openThreeSixtyEvent({
        label: t("carGallery.360"),
        goingToOpen: !threeSixtyOpen,
      })
    );
    setThreeSixtyOpen(!threeSixtyOpen);
  };

  const toggleYouTubeVideoOpen = () => {
    setYouTubeVideoOpen(!youTubeVideoOpen);
  };

  const handleConfigurationDispatch = index => {
    dispatch(updateCarPreconfigurationAction(preconfigurations[index]));
    dispatch(setLastChangedAtAction(Date.now()));
  };

  const handleSlideChange = index => {
    if (currentIndex === index) {
      return;
    }

    setCurrentIndex(index);

    handleConfigurationDispatch(index);
  };

  useEffect(() => {
    setCurrentIndex(
      preconfigurations.findIndex(
        config => config.id === activePreconfiguration.id
      )
    );
  }, [activePreconfiguration.id, preconfigurations]);

  useEffect(() => {
    setYouTubeVideoOpen(false);
    setThreeSixtyOpen(false);
  }, [activePreconfiguration.id]);

  return (
    <div className="relative w-full max-w-160 my-8">
      {threeSixtyOpen && (
        <div className="absolute top-0 left-1/20 size-full max-w-160 lg:left-0">
          <ErrorBoundary>
            <ThreeSixty onClose={toggleThreeSixtyOpen} />
          </ErrorBoundary>
        </div>
      )}
      {youTubeVideoOpen && (
        <div className="flex flex-col gap-4 relative">
          <button
            aria-label={t("basket.back")}
            className="
                grid place-items-center size-8 ml-auto mr-2
                border-2 border-greyLight rounded-sm cursor-pointer
                transition delay-150 hover:scale-110 active:scale-90 hover:border-primaryOrange
              "
            onClick={toggleYouTubeVideoOpen}
          >
            <Icon
              className="size-4"
              icon={<CloseIcon data-id="360-close-icon" />}
            />
          </button>
          <YouTube
            videoId={activePreconfiguration.youTubeVideoId}
            width={584}
            height={328}
          />
        </div>
      )}
      <Carousel
        afterSlide={handleSlideChange}
        currentIndex={currentIndex}
        isHidden={threeSixtyOpen || youTubeVideoOpen}
      >
        {preconfigurations.map(
          ({ label, id, additionalText, youTubeVideoId }) => (
            <div
              className="
                  flex flex-col items-center w-[88svw] p-4 pb-1 mr-12 max-w-140 min-h-88 lg:size-full lg:p-8 lg:m-auto
                "
              key={id}
              tabIndex={-1}
              role="button"
            >
              <div
                className={twMerge(
                  "flex flex-col justify-between items-center w-full rounded-[45%] pb-8 mb-6 relative",
                  (CAR_GALLERY_360_ICON.includes(id) || youTubeVideoId) &&
                    "border-b-2 border-b-greyLight",
                  // !additionalText && "p-2 pb-6",
                  "lg:p-4 lg:h-86 lg:w-4/5 lg:mb-8"
                )}
                withThreeSixtyInfo={additionalText}
                data-id={`car-model-360-offview-${id}`}
              >
                <img
                  alt={label}
                  className="w-9/10 max-w-none lg:w-13/10 lg:-mt-12"
                  onError={event =>
                    event.target.setAttribute(
                      "src",
                      getStaticAssetURL("/images/preconfigs/front-default.png")
                    )
                  }
                  src={getStaticAssetURL(getCarImagePath(id))}
                  title={label}
                />
                {youTubeVideoId ? (
                  <button
                    className="
                        grid place-items-center size-12
                        absolute -bottom-6
                        bg-white border-4 border-current rounded-full shrink-0
                        text-center text-greyLight cursor-pointer
                        transition duration-150
                        hover:scale-105 hover:text-primaryOrange active:scale-95"
                    data-id={`youtube-button-${id}`}
                    onClick={() => toggleYouTubeVideoOpen()}
                    tabIndex={-1}
                  >
                    <Icon
                      className="size-8"
                      icon={<YouTubeIcon data-id={`youtube-button-${id}`} />}
                    />
                  </button>
                ) : (
                  CAR_GALLERY_360_ICON.includes(id) && (
                    <button
                      className="
                          grid place-items-center size-12
                          absolute -bottom-6
                          bg-white border-4 border-current rounded-full shrink-0
                          text-center text-greyLight cursor-pointer
                          transition duration-150
                          hover:scale-105 hover:text-primaryOrange active:scale-95"
                      data-id={`360-in-button-${id}`}
                      onClick={toggleThreeSixtyOpen}
                      tabIndex={-1}
                    >
                      <Icon
                        className="size-8"
                        icon={<Icon360 data-id={`360-icon-in-button-${id}`} />}
                      />
                    </button>
                  )
                )}
              </div>

              <Typography className="pt-4 text-xs text-primaryDark text-center">
                {activePreconfiguration.youTubeVideoId
                  ? t("carGallery.youtube")
                  : CAR_GALLERY_360_ICON.includes(id) && t("carGallery.360")}
              </Typography>

              <Typography className="py-1 text-sm text-center uppercase font-extrabold smallMobile:py-4 lg:text-lg">
                {additionalText}
              </Typography>
            </div>
          )
        )}
      </Carousel>
    </div>
  );
};

export default React.memo(CarGallery);
